import { useState, useCallback } from "react";
import type { OnboardingConfig, Achievement } from "../data/onboardingConfig.types";

interface Celebration {
  achievementId: string;
  points: number;
}

export function useGamification(config: OnboardingConfig) {
  const [totalPoints, setTotalPoints] = useState(0);
  const [unlockedAchievements, setUnlockedAchievements] = useState<string[]>([]);
  const [pendingCelebrations, setPendingCelebrations] = useState<Celebration[]>([]);

  const onStepComplete = useCallback((stepIndex: number) => {
    const step = config.steps[stepIndex];
    if (!step) return;

    setTotalPoints((prev) => prev + step.points);

    if (step.achievementId) {
      setUnlockedAchievements((prev) => {
        if (prev.includes(step.achievementId!)) return prev;
        return [...prev, step.achievementId!];
      });

      if (step.celebrate) {
        setPendingCelebrations((prev) => [
          ...prev,
          { achievementId: step.achievementId!, points: step.points },
        ]);
      }
    }
  }, [config.steps]);

  const consumeCelebration = useCallback(() => {
    setPendingCelebrations((prev) => prev.slice(1));
  }, []);

  const getAchievement = useCallback(
    (id: string): Achievement | undefined => {
      return config.achievements.find((a) => a.id === id);
    },
    [config.achievements]
  );

  return {
    totalPoints,
    maxPoints: config.totalPoints,
    unlockedAchievements,
    pendingCelebrations,
    onStepComplete,
    consumeCelebration,
    getAchievement,
    achievements: config.achievements,
  };
}
